import { RecordKind } from './record.js';
import { BigNumber, EnumType, ObjectType, PrimitiveType, SystemType, TypeKind } from './types.js';
import { Constraints, MinLengthConstraint, RequiredConstraint, UniqueConstraint } from './constraint.js';
import { APIResult } from './plugin.js';
import { DeepModelWalker } from './internal/ModelWalker.js';
import { ExternalFieldCyclicDependenciesValidator } from './validators/ExternalFieldCyclicDependenciesValidator.js';
export class SchemaTypeRef {
    constructor(schemaRef, typeRef) {
        this.schemaRef = schemaRef;
        this.typeRef = typeRef;
    }
}
export class Schema {
    constructor(types = {}) {
        this.kind = RecordKind.Schema;
        this.types = {};
        this.languages = [];
        this.resolutionSchemas = new Map();
        this.staticAPI = new Map();
        this.prototypeAPI = new Map();
        this
            .add(Schema.string_type)
            .add(Schema.boolean_type)
            .add(Schema.ml_string_type)
            .add(Schema.number_type)
            .add(Schema.big_number_type);
        this.addTypes(types);
        this.staticAPI.set('Console', {
            print: (...args) => {
                console.log('Console::print', ...args);
                return APIResult.boolean(true);
            }
        });
    }
    validate() {
        for (let type of Object.values(this.types)) {
            switch (type.kind) {
                case TypeKind.Object:
                    if (type instanceof ObjectType) {
                        type.properties.forEach(p => this.checkPropertyOptions(p));
                        const validator = new ExternalFieldCyclicDependenciesValidator(type, this);
                        validator.validate();
                    }
                    else {
                        throw new Error(`Wrong type declaration for ${type.name}. Object expected`);
                    }
                    break;
            }
        }
    }
    listTypes() {
        return Object.values(this.types);
    }
    addTypes(types) {
        for (let [typeName, value] of Object.entries(types)) {
            if (typeName != value.name) {
                throw new Error(`Wrong schema configuration for type ${typeName} (name mismatch)`);
            }
            this.add(value);
        }
        return this;
    }
    add(...type) {
        type.forEach(v => {
            this.types[v.name] = v;
        });
        return this;
    }
    registerPlugin(plugin) {
        plugin.setup(this);
        const prototypeExtensions = plugin.registerPrototypes();
        this.extendPrototypeRegistryWithExtensions(this.prototypeAPI, prototypeExtensions);
        const staticExtensions = plugin.registerStatics();
        this.extendStaticRegistryWithExtensions(this.staticAPI, staticExtensions);
        return this;
    }
    extendPrototypeRegistryWithExtensions(registry, extensions) {
        extensions.forEach((value, key) => {
            this.extendPrototypeRegistryWithAPI(registry, key, value);
        });
    }
    extendStaticRegistryWithExtensions(registry, extensions) {
        extensions.forEach((value, key) => {
            this.extendStaticRegistryWithAPI(registry, key, value);
        });
    }
    extendPrototypeRegistryWithAPI(registry, typeName, api) {
        if (!registry.has(typeName)) {
            registry.set(typeName, {});
        }
        const extendedAPI = registry.get(typeName) || {};
        const self = this;
        for (let [fnName, fn] of Object.entries(api)) {
            if (extendedAPI[fnName]) {
                throw new Error(`Wrong plugin declaration: Function with ${fnName} already exists`);
            }
            extendedAPI[fnName] = function (...args) {
                const { outputType, outputValue } = fn(this, ...args);
                if (!self.getType(outputType)) {
                    throw new Error(`Wrong plugin function declaration in ${fnName}: ${outputType} is not registered in schema`);
                }
                if (self.prototypeAPI.has(outputType)) {
                    self.extendWithPrototype(outputValue, outputType);
                }
                return outputValue;
            };
        }
        registry.set(typeName, extendedAPI);
    }
    extendStaticRegistryWithAPI(registry, typeName, api) {
        if (!registry.has(typeName)) {
            registry.set(typeName, {});
        }
        const extendedAPI = registry.get(typeName) || {};
        for (let [fnName, fn] of Object.entries(api)) {
            if (extendedAPI[fnName]) {
                throw new Error(`Function with ${fnName} already exists`);
            }
            extendedAPI[fnName] = (...args) => {
                const { outputType, outputValue } = fn(...args);
                if (!this.getType(outputType)) {
                    throw new Error(`${outputType} is not registered in schema`);
                }
                if (this.prototypeAPI.has(outputType)) {
                    this.extendWithPrototype(outputValue, outputType);
                }
                return outputValue;
            };
        }
        registry.set(typeName, extendedAPI);
    }
    getType(type) {
        return this.types[type];
    }
    getConstraint(constraint, kwargs) {
        switch (constraint) {
            case Constraints.MinLength:
                return Object.assign(new MinLengthConstraint(), kwargs);
            case Constraints.Required:
                return Object.assign(new RequiredConstraint(), kwargs);
            case Constraints.UniqueItems:
                return Object.assign(new UniqueConstraint(), kwargs);
        }
    }
    getSchemaTypeReference(type) {
        if (!this.types[type]) {
            throw new Error(`Type does not exist ${type}`);
        }
        return new SchemaTypeRef(this._id, type);
    }
    extendWithPrototypeOneLevelDeep(value, typeRef) {
        let type = this.getType(typeRef);
        if (type.kind == TypeKind.Object || type.kind == TypeKind.List) {
            if (this.prototypeAPI.has(type.name)) {
                const origin = value.__proto__;
                if (origin.extended) {
                    value.__proto__ = this.prototypeAPI.get(type.name);
                }
                else {
                    let proto = this.prototypeAPI.get(type.name);
                    proto.extended = true;
                    proto.__proto__ = origin;
                    value.__proto__ = proto;
                }
            }
        }
    }
    extendWithPrototype(value, typeRef) {
        const walker = new DeepModelWalker(this, value, typeRef);
        let propValue;
        if (this.prototypeAPI.has(typeRef)) {
            this.extendWithPrototypeOneLevelDeep(value, typeRef);
        }
        while (propValue = walker.next()) {
            this.extendWithPrototypeOneLevelDeep(propValue.value, propValue.property.type);
        }
    }
    checkPropertyOptions(prop) {
        const { default: def } = prop.opts;
        if (def != null) {
            const type = this.getType(prop.type);
            switch (type.kind) {
                case TypeKind.Primitive:
                    if (type.getTypeReference() === Schema.big_number_type.getTypeReference()) {
                        if (def instanceof BigNumber) {
                            break;
                        }
                    }
                    let expectedDefaultType;
                    if (type.getTypeReference() === Schema.string_type.getTypeReference()) {
                        expectedDefaultType = 'string';
                    }
                    if (type.getTypeReference() === Schema.ml_string_type.getTypeReference()) {
                        expectedDefaultType = 'string';
                    }
                    if (type.getTypeReference() === Schema.number_type.getTypeReference()) {
                        expectedDefaultType = 'number';
                    }
                    if (type.getTypeReference() === Schema.boolean_type.getTypeReference()) {
                        expectedDefaultType = 'boolean';
                    }
                    if (typeof def !== expectedDefaultType) {
                        throw new Error(`Wrong type definition for ${prop.name} property. Invalid type of default. ${prop.type} expected`);
                    }
                    break;
                case TypeKind.Enum:
                    if (type instanceof EnumType) {
                        if (!type.values.includes(def)) {
                            throw new Error(`Wrong type definition for ${prop.name} property.
               Invalid type of default. ${def} should be in a list of ${type.values} values`);
                        }
                    }
                    else {
                        throw Error(`Wrong type definition for ${type.name}. Enum expected`);
                    }
                    break;
                case TypeKind.Union:
                    break;
                case TypeKind.List:
                case TypeKind.Object:
                default:
                    throw new Error(`Wrong type definition for ${prop.name} property. Impossible to define default for ${prop.type}`);
            }
        }
    }
    addLanguageSupport(...langs) {
        this.languages.push(...langs);
    }
    isLanguageRegistered(lang) {
        return this.languages.indexOf(lang) > -1;
    }
}
Schema.string_type = new PrimitiveType('string', SystemType.String);
Schema.number_type = new PrimitiveType('number', SystemType.Number);
Schema.boolean_type = new PrimitiveType('boolean', SystemType.Boolean);
Schema.ml_string_type = new PrimitiveType('mlstring', SystemType.MLString);
Schema.big_number_type = new PrimitiveType('bignumber', SystemType.BigNumber);
//# sourceMappingURL=schema.js.map